import { Outlet, useMatch } from 'react-router-dom';

import { Skeleton } from '../../components/Skeleton';
import { useAccountingView } from '../../hooks/useAccountingView';

import styles from './Accounting.module.css';

const SUBTITLE =
  'Beta: last 30 days of spend by investment type and accounting treatment.';

/**
 * Accounting feature shell. Renders the page title, then the actual
 * surface — spend Sankey, detail table, and review-queue rail — paints
 * through <Outlet /> from the index route (AccountingOverview).
 * Per-commit audit and per-engineer drill-downs are leaf routes with
 * their own back-link breadcrumb.
 *
 * Lives under the `/accounting` route — see main.tsx.
 */
export function AccountingShell() {
  // Hide the page title while we're on a per-commit audit or per-engineer
  // detail page — those leaves carry their own heading + back link. Both
  // matches must be called unconditionally (rules of hooks).
  const commitMatch = useMatch('/accounting/commit/:sha');
  const engineerMatch = useMatch('/accounting/engineer/:engineerId');
  const isLeaf = commitMatch ?? engineerMatch;

  const { isPending } = useAccountingView();

  // While the view is in flight, show a single page-level skeleton — same
  // simple loading block every other matrix view uses — instead of the
  // overview's three stacked skeletons. Leaf routes keep their own.
  if (isPending && !isLeaf) {
    return <Skeleton title='Accounting' subtitle={SUBTITLE} rows={8} />;
  }

  return (
    <div className={styles.shell}>
      {!isLeaf ? (
        <div className={styles.heading}>
          <h1 className='snap-h1'>Accounting</h1>
          <p className='snap-subtitle'>{SUBTITLE}</p>
        </div>
      ) : null}

      <Outlet />
    </div>
  );
}
